import { ClerkProvider } from "@clerk/react-router";
import { clerkMiddleware, rootAuthLoader } from "@clerk/react-router/server";
import * as Sentry from "@sentry/react-router";
import { App as AntdApp, ConfigProvider } from "antd";
import clsx from "clsx";
import { useCallback, useEffect } from "react";
import {
  isRouteErrorResponse,
  Links,
  Meta,
  type MetaFunction,
  Outlet,
  Scripts,
  ScrollRestoration,
  useMatches,
  useRouteLoaderData,
} from "react-router";
import getServerConfig from "server/config";
import type { Route } from "./+types/root";
import secondcropLogo from "./assets/compact-logo.png";
import UserService from "./modules/auth/UserService";
import { HEADER_HEIGHT, THEME_COLORS } from "./modules/constants";
import "./app.css";
import "./styles/clerkOverrides.css";
import "./styles/sentryOverrides.css";
import "./styles/antd.css";
import { NavigationProgress } from "./components/NavigationProgress";
import { regionSchema } from "./modules/auth/schema";

declare global {
  interface Window {
    umami?: {
      identify: (properties: Record<string, string | number | boolean>) => void;
    };
    Helpkit?: {
      show: (
        view: "popover" | "sidebar",
        type?: string,
        articleId?: string,
      ) => void;
      hide: () => void;
      hideLaunchButton: () => void;
      showLaunchButton: () => void;
    };
  }
}

export const middleware: Route.MiddlewareFunction[] = [clerkMiddleware()];

export async function loader(args: Route.LoaderArgs) {
  return rootAuthLoader(args, async () => {
    const serverConfig = getServerConfig();
    const user = await UserService.getCurrentUser(args);
    const url = new URL(args.request.url);
    const deployment: "production" | "staging" | "development" =
      serverConfig.NODE_ENV === "production"
        ? url.hostname === "staging.app.secondcrop.com"
          ? "staging"
          : "production"
        : "development";

    return {
      ENV: {
        // TODO: we could consider using VITE_ prefix and import.meta.env
        // for these values
        ARCGIS_CLIENT_API_KEY: serverConfig.ARCGIS_CLIENT_API_KEY,
        GIT_COMMIT_HASH: serverConfig.GIT_COMMIT_HASH,
        NODE_ENV: process.env.NODE_ENV,
        SECONDCROP_FIELD_MAP_ATTACHMENTS_MAX:
          serverConfig.SECONDCROP_FIELD_MAP_ATTACHMENTS_MAX,
        MAPBOX_PUBLIC_KEY: serverConfig.MAPBOX_PUBLIC_KEY,
        ANALYTICS_ID: serverConfig.ANALYTICS_ID,
      },
      deployment,
      isInternal: user !== null ? user.isAdmin : null,
      role: user?.role ?? null,
      region: user?.region ?? null,
    };
  });
}

export const meta: MetaFunction = () => {
  return [{ title: "SecondCrop Soil Carbon Projects | Loam Bio Australia" }];
};

export function Layout({ children }: { children: React.ReactNode }) {
  const loaderData = useRouteLoaderData<typeof loader>("root");

  const matches = useMatches();
  const hideHelpKit = matches.some(
    (m) => (m.handle as { hideHelpKit?: boolean } | undefined)?.hideHelpKit,
  );

  const setLaunchButtonVisibility = useCallback((show: boolean) => {
    if (show) {
      window.Helpkit?.showLaunchButton();
    } else {
      window.Helpkit?.hideLaunchButton();
    }
  }, []);

  useEffect(() => {
    // Always hide HelpKit outside US
    const visible = loaderData?.region === regionSchema.enum.US && !hideHelpKit;

    // HelpKit's hideLaunchButton/showLaunchButton query the DOM directly and
    // silently no-op (caught internally) if .helpkit-launcherButton isn't
    // mounted yet, so we wait for the actual node rather than window.Helpkit.
    if (document.querySelector(".helpkit-launcherButton")) {
      setLaunchButtonVisibility(visible);
      return;
    }

    const observer = new MutationObserver(() => {
      if (document.querySelector(".helpkit-launcherButton")) {
        setLaunchButtonVisibility(visible);
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [loaderData?.region, hideHelpKit, setLaunchButtonVisibility]);

  useEffect(() => {
    if (!window.__sentryInitialized && loaderData) {
      console.info(
        `init sentry client: ${loaderData.deployment} v${loaderData.ENV.GIT_COMMIT_HASH}`,
      );

      Sentry.init({
        dsn: "https://4b7a324bb74bc6106b888623a70f5c7f@o4506862555365376.ingest.us.sentry.io/4509767678558208",
        tracesSampleRate: 1,
        replaysSessionSampleRate: 0.1,
        replaysOnErrorSampleRate: 1,
        enabled:
          typeof loaderData.ENV.NODE_ENV === "string" &&
          loaderData.ENV.NODE_ENV !== "development",
        environment: loaderData.deployment,
        release: loaderData.ENV.GIT_COMMIT_HASH,
        integrations: [
          Sentry.browserTracingIntegration(),
          Sentry.replayIntegration(),
        ],
      });

      window.__sentryInitialized = true;
    }
  }, [loaderData]);

  useEffect(() => {
    if (
      loaderData?.isInternal !== null &&
      loaderData?.isInternal !== undefined &&
      loaderData?.role !== null &&
      loaderData?.role !== undefined
    ) {
      window.umami?.identify({
        userType: loaderData.isInternal ? "internal" : "external",
        role: loaderData.role,
      });
    }
  }, [loaderData?.isInternal, loaderData?.role]);

  return (
    <html
      lang="en"
      style={{ "--header-offset": `${HEADER_HEIGHT}px` } as React.CSSProperties}
    >
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
        {loaderData && (
          <script
            // biome-ignore lint/security/noDangerouslySetInnerHtml: Migrate to biome
            dangerouslySetInnerHTML={{
              __html: `window.ENV = ${JSON.stringify({ ...loaderData.ENV, deployment: loaderData.deployment })}`,
            }}
          />
        )}

        {loaderData && loaderData.ENV.ANALYTICS_ID.length > 0 && (
          <script
            defer
            src="https://cloud.umami.is/script.js"
            data-website-id={loaderData.ENV.ANALYTICS_ID}
          />
        )}

        <script
          id="helpkit-widget"
          data-project-id="secondcrophelp"
          type="text/javascript"
          src="https://www.helpkit.so/widget/script.js"
          async
        />
      </body>
    </html>
  );
}

export default function App({ loaderData }: Route.ComponentProps) {
  return (
    <ConfigProvider
      theme={{
        token: {
          colorPrimary: THEME_COLORS.loamGreen,
          colorLink: "inherit",
          colorLinkHover: THEME_COLORS.loamGreen,
        },
        components: {
          Layout: {
            siderBg: "#fff",
            headerBg: "#fff",
            headerHeight: HEADER_HEIGHT,
            headerPadding: "0 24px",
          },
        },
      }}
    >
      <NavigationProgress />
      {/* Environment tag */}
      {["staging", "development"].includes(loaderData.deployment) && (
        <div
          className={clsx(
            "fixed top-0 right-0 text-white px-2 py-1 z-env-badge print:hidden",
            {
              "bg-yellow-400": loaderData.deployment === "staging",
              "bg-green-400": loaderData.deployment === "development",
            },
          )}
        >
          {loaderData.deployment === "staging" ? "staging" : "local"}
        </div>
      )}
      <AntdApp>
        <ClerkProvider loaderData={loaderData}>
          <Outlet />
        </ClerkProvider>
      </AntdApp>
    </ConfigProvider>
  );
}

export function ErrorBoundary({ error, loaderData }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;
  const deployment = loaderData?.deployment;

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (error && error instanceof Error) {
    // you only want to capture non 404-errors that reach the boundary
    Sentry.captureException(error);
    if (deployment !== "production") {
      details = error.message;
      stack = error.stack;
    }
  }

  return (
    <main className="pt-16 p-4 container mx-auto">
      <div className="w-full flex flex-col items-center gap-y-4">
        <img src={secondcropLogo} alt="SecondCrop" className="w-auto h-12" />
        <h1 className="font-bold text-xl">{message}</h1>
        <p className="text-lg">{details}</p>
        <a
          href="/"
          className="inline-block p-2 bg-loamGreen text-white rounded-md"
        >
          Go back to home
        </a>
      </div>

      <pre className="w-full p-4 overflow-x-auto">
        <pre>Deployment: {deployment}</pre>
        {stack && deployment !== "production" && <code>{stack}</code>}
      </pre>
    </main>
  );
}
